import { Constants } from "../../../fetch-xml";
import { IColumn, IDataset } from "../../interfaces";
import { DataProvider } from "../DataProvider";
import { DeepPartial, IDataProvider, IRecord } from "../interfaces";
import { Record } from '../Record/Record';
import { DataBuilder } from "./DataBuilder";

type IMemoryProviderEntityMetadata = DeepPartial<Xrm.Metadata.EntityMetadata> & {
    PrimaryIdAttribute: string
}
/**
 * Allows you to use in-memory array as Data Source.
 *
 */
export class MemoryDataProvider extends DataProvider implements IDataProvider {
    private _title: string = "";
    private _dataSource: { [key: string]: any }[] = [];
    private _currentData: { [key: string]: any }[] = [];
    private _columns: IColumn[] = [];
    private _pageNumber: number = 1;
    private _pageSize: number = Constants.DEFAULT_PAGE_SIZE_SUBGRID;
    private _totalRecordCount: number = 0;
    //@ts-ignore needs to be defined through setMetadata
    private _entityMetadata: IMemoryProviderEntityMetadata;
    /**
     * Creates an instance of MemoryDataProvider.
     */
    constructor(dataSource: { [key: string]: any }[]) {
        super();
        this._dataSource = dataSource;
    }

    public setColumns(columns: IColumn[]) {
        this._columns = columns;
    }

    public getColumns() {
        return this._columns;
    }

    public getPaging(): IDataset['paging'] {
        return {
            firstPageNumber: this._pageNumber,
            hasNextPage: this._pageNumber * this._pageSize < this._totalRecordCount,
            hasPreviousPage: this._pageNumber > 1,
            lastPageNumber: this._pageNumber,
            pageNumber: this._pageNumber,
            pageSize: this._pageSize,
            totalResultCount: this._totalRecordCount,
            loadExactPage: async (pageNumber: number) => {
                this._pageNumber = pageNumber;
                await this._fetchData();
                return this.getRecords();
            },
            loadNextPage: async () => {
                this._pageNumber++;
                await this._fetchData();
                return this.getRecords();
            },
            loadPreviousPage: async () => {
                this._pageNumber--;
                await this._fetchData();
                return this.getRecords();
            },
            reset: () => {
                this.refresh();
            },
            setPageSize: (pageSize) => {
                this._pageSize = pageSize;
            }
        };
    }
    public refresh(): IRecord[] {
        this._pageNumber = 1;
        this._fetchData();
        return this.getRecords();
    }

    public getRecords(): IRecord[] {
        return this._currentData.map(x => {
            const recordId = x[this._primaryIdAttribute];
            return new Record(recordId, this);
        });
    }
    public getRawRecordData(recordId: string): { [key: string]: any; } | null {
        return this._dataSource.find(x => x[this._primaryIdAttribute] === recordId) ?? null;
    }

    public setDataSource(dataSource: { [key: string]: any }[]): void {
        this._dataSource = dataSource;
    }
    public getDataSource() {
        return this._dataSource;
    }

    public isError(): boolean {
        return false;
    }

    public getErrorMessage(): string {
        return "";
    }
    public getTitle(): string {
        return this._title;
    }

    public setTitle(title: string) {
        this._title = title;
    }

    public save(record: IRecord): Promise<void> {
        throw new Error('Not implemented!');
    }

    public openDatasetItem() {
        throw new Error('Not implemented!');
    }

    public getMetadata(): IMemoryProviderEntityMetadata {
        return this._entityMetadata as any;
    }

    public setMetadata(metadata: IMemoryProviderEntityMetadata) {
        this._entityMetadata = metadata;
    }

    public getEntityName() {
        return "";
    }

    public getViewId() {
        return "";
    }

    private async _fetchData() {
        this._currentData = new DataBuilder(this._primaryIdAttribute, this._dataSource, this)
            .setFulltextSearch(this.getSearchQuery() ?? undefined)
            .setFiltering(this.getFiltering())
            .setSorting(this.getSorting())
            .getData();
        this._totalRecordCount = this._currentData.length;
        const skip = this._pageNumber === 1 ? 0 : (this._pageNumber - 1) * this._pageSize;
        this._currentData = this._currentData.slice(skip, skip + this._pageSize);
    }

    private get _primaryIdAttribute() {
        if (!this._entityMetadata?.PrimaryIdAttribute) {
            throw new Error("MemoryDataProvider: Missing PrimaryIdAttribute name in metadata. You need to specify it through setMetadata before any data is fetched.");
        }
        return this._entityMetadata.PrimaryIdAttribute;
    }

    public static GetParsedData(data: string | null): { [key: string]: any }[] {
        try {
            return JSON.parse(data ?? "[]");
        }
        catch (err) {
            return [];
        }
    }
}